import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import type {
  ArtifactKind,
  DocChatSearchResult,
  DocChatSseErrorEvent,
  DocChatSseResponsePartialEvent,
  DocChatSseThoughtEvent,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { SelectedTab } from 'features/navbar/types'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'
import { z } from 'zod'

import type { NodeFocusRequest } from './graph/NodeFocusRequestContext'
import type { NormalizedBBox } from './types/ocrViewer'
export type ViewMode = 'card' | 'table'
export const documentSearchModeSchema = z.enum(['normal', 'ai'])
export type DocumentSearchMode = z.infer<typeof documentSearchModeSchema>

export enum DocumentViewerPage {
  LEFT_PAGE = 0,
  RIGHT_PAGE = 1,
}

/**
 * Turns are persisted to session storage, so this carries an id to resolve the
 * thumbnail against in-memory attachments — never the base64 image itself.
 */
export interface DocChatSubmittedQueryAttachment {
  id: string
  name: string
}

export interface DocChatSubmittedQueryEvent {
  id: string
  type: 'submitted_query'
  query: string
  attachments?: DocChatSubmittedQueryAttachment[]
}

export interface DocChatThinkingEvent {
  id: string
  type: 'thinking'
  query: string
}

export type DocChatThoughtEvent = DocChatSseThoughtEvent & {
  id: string
}

export type DocChatResponseEvent = Omit<DocChatSearchResult, 'results'> & {
  id: string
  resultCount: number
  /** Absent on conversations persisted before per-turn results shipped. */
  results?: DocChatSearchResult['results']
}

export type DocChatResponsePartialEvent = DocChatSseResponsePartialEvent & {
  id: string
}

export type DocChatErrorEvent = DocChatSseErrorEvent & {
  id: string
}

export interface DocChatCancelledEvent {
  id: string
  type: 'cancelled'
  query: string
}

/** Timeline marker for a produced document; the artifact itself lives in the artifact store. */
export interface DocChatArtifactEvent {
  id: string
  type: 'artifact'
  artifact_id: string
  kind: ArtifactKind
  title: string
  revision: number
  created: boolean
  missing_count: number
  changed_paths: string[]
}

export type DocChatEvent =
  | DocChatSubmittedQueryEvent
  | DocChatThinkingEvent
  | DocChatThoughtEvent
  | DocChatResponsePartialEvent
  | DocChatResponseEvent
  | DocChatErrorEvent
  | DocChatCancelledEvent
  | DocChatArtifactEvent

export type DocChatTurnStatus = 'loading' | 'complete' | 'error' | 'cancelled'

export interface DocChatTurn {
  id: string
  query: string
  /** Sent to the agent with the query; never rendered. */
  hiddenContext?: string
  /** Structured tag picks sent with the turn; label kept for the picker UI. */
  tagSelections?: DocChatTagSelection[]
  /**
   * Overlay from entry `active_turn=running`. A later client send must not
   * treat this as an abandoned local stream.
   */
  restoredRunning?: boolean
  requestStarted?: boolean
  pendingResponse?: DocChatResponseEvent
  events: DocChatEvent[]
}

export interface DocChatTagSelection {
  identifier: string
  entryId: string
  label: string
}

export interface DocChatConversation {
  id?: string
  pendingSessionKey?: string
  activeTurnId?: string
  turns: DocChatTurn[]
}

export interface DocChatQueuedMessage {
  id: string
  query: string
  attachments?: DocChatSubmittedQueryAttachment[]
  hiddenContext?: string
  tagSelections?: DocChatTagSelection[]
  isEditing?: boolean
}

export interface DestinationHistoryItem {
  type: 'destination'
  destinationKey: string
  label: string
  href: string
}

export type AgenticChatLayout = 'docked' | 'floating'

export interface SearchHistoryItem {
  type: 'search'
  searchState: {
    contentFilter: DocumentsViewerContentFilter
    searchMode: DocumentSearchMode
    viewMode: ViewMode
    query: string
    inputQuery?: string
    browseAll?: boolean
    filters: ColumnFiltersState
    sorting: SortingState
    pagination: PaginationState
    selectedDocumentId: string | null
    chatOnly?: boolean
    chatLayout?: AgenticChatLayout
    docChat?: {
      siteId: string
      conversation: DocChatConversation
      queuedMessages?: DocChatQueuedMessage[]
      /** Restored turns must not be re-submitted as a new DocChat query. */
      isHydratedHistory?: boolean
      previewDocumentId?: string
      previewPage?: number
      artifactId?: string
    }
  }
  linkedDocuments?: {
    referenceText: string
    documentIds: string[]
    unitId?: string
  }
}

export type SearchStateUpdate =
  | Partial<SearchHistoryItem['searchState']>
  | ((
      currentSearchState: SearchHistoryItem['searchState']
    ) => Partial<SearchHistoryItem['searchState']>)

export interface FragmentHighlight {
  pageNum: number
  fragmentText: string
  quotes?: string[]
  sourceVersion?: string
  cmmsDocumentRevision?: string
  cmmsNodeIds?: string[]
}

/** Cited region as normalized page-fraction rects (no OCR block ids required). */
export interface RegionHighlight {
  pageNum: number
  boxes: NormalizedBBox[]
}

/** Origin of a doc-reference jump for "Back to …" affordance. */
export interface ReferenceJumpOrigin {
  documentId: string
  label: string
}

export interface LotoPanelState {
  activeReportId?: string
  focusNode?: NodeFocusRequest
}

export interface DocumentHistoryItem {
  type: 'document'
  documentId: string
  pageNumber: number
  selectedBoxIds: string[]
  selectedSubjectUid?: string
  selectedGraphNodeId?: string
  lotoPanel?: LotoPanelState
  query: string
  searchOriginQuery?: string
  fragmentHighlight?: FragmentHighlight
  regionHighlight?: RegionHighlight
  referenceOrigin?: ReferenceJumpOrigin
}

export interface ArtifactHistoryItem {
  type: 'artifact'
  artifactId: string
}

export type TabHistoryItem =
  | SearchHistoryItem
  | DocumentHistoryItem
  | DestinationHistoryItem
  | ArtifactHistoryItem

export interface DocumentTab {
  id: string
  history: TabHistoryItem[]
  currentIndex: number
  customLabel?: string
  /** Unsaved-edits marker; dirty tabs are not reused for sidebar destinations. */
  dirty?: boolean
  workspaceDestination?: SelectedTab
  /** When false, tab is pinned (not closable/draggable). */
  closable?: boolean
  /** Stable dedup key for resource-opening APIs. */
  resourceKey?: string
  /** Recency for unused New chats; survives workspace tab restore. */
  openedAt?: number
}

export const viewLayoutSchema = z.enum(['continuous', 'twoPage', 'single'])
export type ViewLayout = z.infer<typeof viewLayoutSchema>

export enum TooltipOption {
  ShowTooltips = 'showTooltips',
  ShowMultiplePages = 'showMultiplePages',
  MultiPageTurner = 'multiPageTurner',
  AutoOpenNewTab = 'autoOpenNewTab',
}

export enum FilterOption {
  ShowUnconfirmed = 'showUnconfirmed',
  ShowConfirmed = 'showConfirmed',
  ShowAll = 'showAll',
  RemoveBoxes = 'removeBoxes',
}
